import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import {
  COLOR_FAMILIES,
  suggestColorFamily,
  type CatalogOptionDto,
  type CatalogOptionValueDto,
  type ColorFamilyCode,
  type OptionValueInput,
  type ProductDetailDto,
  type ProductOptionDto,
} from '@ecom/contracts';
import { useCatalogStore } from '@/stores/catalog.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { halalasToRiyals, riyalsToHalalas, thousandthsToUnits, unitsToThousandths } from '@/lib/price-input';
import { COLOR_OPTION, colourCode, fabricsOf } from '@/lib/fabrics';
import { planOption, planValues, valuesOf, variantsWith, type PlannedUpdate, type PlannedVariant } from '@/lib/variant-matrix';
import { CheckIcon, PencilIcon, PlusIcon, SearchIcon, TrashIcon } from '@/components/icons';
import { Section, Sheet, Table, Td, Th, Thumb } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Field, Input, Pill, Select, Switch, cx } from '@/components/ui';

/**
 * The ways a product comes, picked from the store's library (ADR-052).
 *
 * Each option the product uses is a row of its values — colours as swatches,
 * ways of selling as chips — in the library's order. Adding a value opens the
 * library, searched on the server, where a new colour can be made on the spot;
 * then the variants it brings are laid out for review — SKUs, prices, what each
 * holds — before anything is written. A value's chip opens what the product
 * says about it: its own name if the library's does not suit, its photos, and
 * removing it with its variants.
 */
export function OptionsEditor({ product }: { product: ProductDetailDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { libraryOptions, loadLibraryOptions } = useCatalogStore();
  const [adding, setAdding] = useState<{ option: CatalogOptionDto; newOption: boolean } | null>(null);
  const [open, setOpen] = useState<{ axis: ProductOptionDto; value: string } | null>(null);
  const [choosingOption, setChoosingOption] = useState(false);

  useEffect(() => {
    void loadLibraryOptions();
  }, [loadLibraryOptions, locale]);

  // The product's options in the library's order; an API older than the
  // library reads them off the variants.
  const axes: ProductOptionDto[] = useMemo(
    () =>
      product.options ??
      [...new Set(product.variants.flatMap((variant) => Object.keys(variant.options)))].map((code) => ({
        code,
        name: code,
        display: code === COLOR_OPTION ? 'SWATCH' : 'BUTTONS',
      })),
    [product],
  );
  const unused = libraryOptions.filter((option) => option.isActive && !axes.some((axis) => axis.code === option.code));
  const labelOf = (option: string, value: string) =>
    product.optionValues.find((entry) => entry.option === option && entry.value === value);

  return (
    <Section
      title={t('productOptions')}
      subtitle={t('productOptionsHint')}
      flush
      action={
        unused.length > 0 ? (
          <Button variant="secondary" size="sm" onClick={() => setChoosingOption(true)}>
            <PlusIcon className="size-4" />
            {t('addOption')}
          </Button>
        ) : (
          <Link to="/options" className="text-xs text-brand-700 hover:underline">
            {t('manageLibrary')}
          </Link>
        )
      }
    >
      {axes.length === 0 ? (
        <div className="border-t border-line">
          <EmptyState title={t('noProductOptions')} body={t('noProductOptionsHint')} />
        </div>
      ) : (
        <ul className="divide-y divide-line border-t border-line">
          {axes.map((axis) => {
            const libraryOption = libraryOptions.find((option) => option.code === axis.code);
            const values = valuesOf(product, axis.code);
            return (
              <li key={axis.code} className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-start">
                <div className="w-40 shrink-0">
                  <p className="text-sm font-semibold text-ink">{axis.name}</p>
                  <p className="text-xs text-ink-muted">
                    {t('optionValueCount', { count: count(values.length, locale) })}
                  </p>
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                  {values.map((value) => {
                    const entry = labelOf(axis.code, value);
                    const swatch = axis.display === 'SWATCH';
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setOpen({ axis, value })}
                        title={entry?.overridden ? t('ownNameOnProduct') : undefined}
                        className="group inline-flex items-center gap-2 rounded-full border border-line bg-surface py-1 ps-1 pe-3 text-sm text-ink transition-colors hover:border-brand-300 hover:bg-brand-50/50"
                      >
                        {swatch ? (
                          <span
                            className="size-6 rounded-full border border-line"
                            style={{ backgroundColor: entry?.swatchHex ?? 'transparent' }}
                          />
                        ) : (
                          <span className="w-1" />
                        )}
                        <span>{entry?.label ?? value}</span>
                        {entry?.overridden ? <PencilIcon className="size-3 text-brand-600" /> : null}
                        <span data-numeric className="text-[11px] text-ink-faint">
                          {count(variantsWith(product, axis.code, value).length, locale)}
                        </span>
                      </button>
                    );
                  })}
                  {libraryOption ? (
                    <Button variant="ghost" size="sm" onClick={() => setAdding({ option: libraryOption, newOption: false })}>
                      <PlusIcon className="size-3.5" />
                      {axis.display === 'SWATCH' ? t('addColour') : t('addValue')}
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Sheet open={choosingOption} title={t('addOption')} subtitle={t('addOptionHint')} onClose={() => setChoosingOption(false)}>
        <ul className="flex flex-col gap-2">
          {unused.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                onClick={() => {
                  setChoosingOption(false);
                  setAdding({ option, newOption: true });
                }}
                className="flex w-full items-center justify-between gap-3 rounded-control border border-line px-4 py-3 text-start transition-colors hover:border-brand-300 hover:bg-brand-50/40"
              >
                <span>
                  <span className="block text-sm font-medium text-ink">{option.name}</span>
                  <code dir="ltr" className="text-xs text-ink-faint">
                    {option.code}
                  </code>
                </span>
                <span className="text-xs text-ink-muted">{t('optionValueCount', { count: count(option.valueCount, locale) })}</span>
              </button>
            </li>
          ))}
        </ul>
      </Sheet>

      {adding ? (
        <AddValuesSheet
          product={product}
          option={adding.option}
          newOption={adding.newOption}
          onClose={() => setAdding(null)}
        />
      ) : null}

      {open ? <ValueSheet product={product} axis={open.axis} value={open.value} onClose={() => setOpen(null)} /> : null}
    </Section>
  );
}

// ─── Adding values ───────────────────────────────────────────────────────────

/**
 * Pick from the library, then review what it makes. Two steps in one panel so
 * the operator sees the thirty-five variants a click would create before they
 * exist — the reference dashboards' "these variants will be added".
 */
function AddValuesSheet({
  product,
  option,
  newOption,
  onClose,
}: {
  product: ProductDetailDto;
  option: CatalogOptionDto;
  newOption: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { applyVariantPlan, saving, error } = useCatalogStore();
  const swatch = option.display === 'SWATCH';
  const used = useMemo(() => new Set(valuesOf(product, option.code)), [product, option.code]);

  const [picked, setPicked] = useState<CatalogOptionValueDto[]>([]);
  const [plan, setPlan] = useState<{ updates: PlannedUpdate[]; creates: PlannedVariant[] } | null>(null);

  const review = () =>
    setPlan(
      newOption ? planOption(product, option.code, picked) : { updates: [], creates: planValues(product, option.code, picked, swatch) },
    );

  const nameOf = (optionCode: string, value: string) =>
    picked.find((entry) => optionCode === option.code && entry.code === value)?.name ??
    product.optionValues.find((entry) => entry.option === optionCode && entry.value === value)?.label ??
    value;

  const total = plan ? plan.creates.length : 0;
  const order = (product.options ?? []).map((entry) => entry.code);
  const optionRank = (code: string) => (order.includes(code) ? order.indexOf(code) : code === option.code ? -1 : order.length);

  return (
    <Sheet
      open
      size="xl"
      title={swatch ? t('addColoursTitle') : t('addValuesTitle', { option: option.name })}
      subtitle={plan ? t('reviewVariantsHint') : t('pickFromLibraryHint')}
      onClose={onClose}
      footer={
        plan ? (
          <>
            {error ? (
              <span className="me-auto">
                <Alert>{error}</Alert>
              </span>
            ) : (
              <span className="me-auto text-sm text-ink-muted">{t('newColourStock')}</span>
            )}
            <Button variant="secondary" onClick={() => setPlan(null)} disabled={saving}>
              {t('back')}
            </Button>
            <Button
              disabled={saving || (total === 0 && plan.updates.length === 0) || plan.creates.some((row) => !row.sku.trim())}
              onClick={async () => {
                if (await applyVariantPlan(product.id, plan)) onClose();
              }}
            >
              {saving ? t('saving') : t('createVariants', { count: count(total, locale) })}
            </Button>
          </>
        ) : (
          <>
            <span className="me-auto text-sm text-ink-muted">
              {picked.length > 0 ? t('pickerSelected', { count: count(picked.length, locale) }) : null}
            </span>
            <Button variant="secondary" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button disabled={picked.length === 0} onClick={review}>
              {t('reviewVariants')}
            </Button>
          </>
        )
      }
    >
      {plan ? (
        <div className="flex flex-col gap-4">
          {plan.updates.length > 0 ? (
            <Alert tone="info">{t('optionAddedToAll', { count: count(plan.updates.length, locale), value: picked[0]?.name ?? '' })}</Alert>
          ) : null}
          {plan.creates.length === 0 ? (
            <Alert tone="info">{t('nothingNewToCreate')}</Alert>
          ) : (
            <div className="overflow-x-auto rounded-control border border-line">
              <Table>
                <thead>
                  <tr>
                    <Th>{t('variant')}</Th>
                    <Th>{t('sku')}</Th>
                    <Th>{t('price')}</Th>
                    {product.measureUnit ? <Th>{t('measureHolds').replace('{unit}', t(`unit_${product.measureUnit}`))}</Th> : null}
                  </tr>
                </thead>
                <tbody>
                  {plan.creates.map((row, index) => (
                    <tr key={row.key}>
                      <Td>
                        <span className="flex flex-wrap items-center gap-1.5 text-sm text-ink">
                          {/* In the product's option order: the colour, then how it is sold. */}
                          {Object.entries(row.options)
                            .sort(([a], [b]) => optionRank(a) - optionRank(b))
                            .map(([name, value]) => (
                              <span key={name}>{nameOf(name, value)}</span>
                            ))}
                          {row.isSample ? <Pill tone="success">{t('sampleVariant')}</Pill> : null}
                        </span>
                      </Td>
                      <Td>
                        <Input
                          dir="ltr"
                          aria-label={t('sku')}
                          value={row.sku}
                          onChange={(event) =>
                            setPlan((current) =>
                              current && {
                                ...current,
                                creates: current.creates.map((entry, at) => (at === index ? { ...entry, sku: event.target.value } : entry)),
                              },
                            )
                          }
                          className="h-9 min-h-9 w-52 font-mono text-xs"
                        />
                      </Td>
                      <Td>
                        <PriceCell
                          value={row.price}
                          basePrice={product.basePrice}
                          onChange={(price) =>
                            setPlan((current) =>
                              current && {
                                ...current,
                                creates: current.creates.map((entry, at) => (at === index ? { ...entry, price } : entry)),
                              },
                            )
                          }
                        />
                      </Td>
                      {product.measureUnit ? (
                        <Td>
                          <span data-numeric dir="ltr" className="text-sm text-ink-muted">
                            {row.measureQuantity ? `${thousandthsToUnits(row.measureQuantity)} ${t(`unit_${product.measureUnit}`)}` : '—'}
                          </span>
                        </Td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      ) : (
        <LibraryPicker option={option} excluded={used} picked={picked} onPickedChange={setPicked} />
      )}
    </Sheet>
  );
}

/**
 * The library's values for one option, to pick from: searched on the server a
 * page at a time, a colour made on the spot when it is not there yet, and the
 * values already taken shown but not offered. Shared by the Options card and
 * the new-product builder.
 */
export function LibraryPicker({
  option,
  excluded,
  picked,
  onPickedChange,
}: {
  option: CatalogOptionDto;
  /** Codes that cannot be picked — already on the product. */
  excluded: Set<string>;
  picked: CatalogOptionValueDto[];
  /** A state setter: picks are applied as updates, so two quick taps both count. */
  onPickedChange: Dispatch<SetStateAction<CatalogOptionValueDto[]>>;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { searchLibraryValues } = useCatalogStore();
  const swatch = option.display === 'SWATCH';
  const used = excluded;
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<CatalogOptionValueDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Searched on the server, debounced — a colour library outgrows one page.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      void searchLibraryValues(option.id, query).then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setCursor(page.nextCursor);
        setLoading(false);
      });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [option.id, query, searchLibraryValues]);

  const toggle = (value: CatalogOptionValueDto) =>
    onPickedChange((current) =>
      current.some((entry) => entry.id === value.id) ? current.filter((entry) => entry.id !== value.id) : [...current, value],
    );

  return (
    <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-56 flex-1">
              <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
                <SearchIcon className="size-4" />
              </span>
              <Input
                type="search"
                value={query}
                placeholder={t('searchLibrary')}
                aria-label={t('searchLibrary')}
                onChange={(event) => setQuery(event.target.value)}
                className="ps-9"
              />
            </div>
            <Button variant="secondary" onClick={() => setCreating(true)}>
              <PlusIcon className="size-4" />
              {swatch ? t('newColourInLibrary') : t('newValueInLibrary')}
            </Button>
          </div>

          {creating ? (
            <NewLibraryValue
              option={option}
              onCancel={() => setCreating(false)}
              onCreated={(value) => {
                setCreating(false);
                setItems((current) => [value, ...current]);
                onPickedChange((current) => [...current, value]);
              }}
            />
          ) : null}

          {loading && items.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('loading')}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('libraryNothingFound')}</p>
          ) : (
            <div className={cx('grid gap-2', swatch ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2')}>
              {items.map((value) => {
                const already = used.has(value.code);
                const selected = picked.some((entry) => entry.id === value.id);
                const family = COLOR_FAMILIES.find((entry) => entry.code === value.family);
                return (
                  <button
                    key={value.id}
                    type="button"
                    disabled={already}
                    aria-pressed={selected}
                    onClick={() => toggle(value)}
                    className={cx(
                      'relative flex items-center gap-3 rounded-control border-2 p-2.5 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                      selected ? 'border-brand-600 bg-brand-50/60' : 'border-line hover:border-brand-300',
                    )}
                  >
                    {swatch ? (
                      <span className="size-9 shrink-0 rounded-full border border-line" style={{ backgroundColor: value.swatchHex ?? 'transparent' }} />
                    ) : null}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">{value.name}</span>
                      <span className="block truncate text-[11px] text-ink-muted">
                        {already
                          ? t('alreadyOnProduct')
                          : swatch
                            ? (family?.[locale] ?? t('noFamily'))
                            : value.measureQuantity
                              ? t('holdsAmount', { amount: thousandthsToUnits(value.measureQuantity) })
                              : value.isSample
                                ? t('sampleVariant')
                                : value.code}
                      </span>
                    </span>
                    {selected ? (
                      <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-brand-600 text-brand-ink">
                        <CheckIcon className="size-3.5" />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}

          {cursor ? (
            <Button
              variant="ghost"
              size="sm"
              className="self-center"
              onClick={async () => {
                const page = await searchLibraryValues(option.id, query, cursor);
                setItems((current) => [...current, ...page.items]);
                setCursor(page.nextCursor);
              }}
            >
              {t('loadMore')}
            </Button>
          ) : null}
        </div>
  );
}

/** A price typed in riyals, empty for "follows the base price". */
function PriceCell({ value, basePrice, onChange }: { value: number | null; basePrice: number; onChange: (price: number | null) => void }) {
  const t = useT();
  const [text, setText] = useState(value === null ? '' : halalasToRiyals(value));
  const bad = text.trim() !== '' && riyalsToHalalas(text) === null;
  return (
    <Input
      dir="ltr"
      inputMode="decimal"
      aria-label={t('price')}
      value={text}
      invalid={bad}
      placeholder={halalasToRiyals(basePrice)}
      onChange={(event) => {
        setText(event.target.value);
        const next = event.target.value.trim() === '' ? null : riyalsToHalalas(event.target.value);
        if (event.target.value.trim() === '' || next !== null) onChange(next);
      }}
      className="h-9 min-h-9 w-28"
    />
  );
}

/** A value made inside a product: its names, and a swatch and family or what it holds. */
function NewLibraryValue({
  option,
  onCancel,
  onCreated,
}: {
  option: CatalogOptionDto;
  onCancel: () => void;
  onCreated: (value: CatalogOptionValueDto) => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { createLibraryValue, saving, error } = useCatalogStore();
  const swatch = option.display === 'SWATCH';
  const [en, setEn] = useState('');
  const [ar, setAr] = useState('');
  const [hex, setHex] = useState('#8b5e3c');
  const [family, setFamily] = useState<ColorFamilyCode | ''>('');
  const [holds, setHolds] = useState('');
  const [isSample, setIsSample] = useState(false);
  const suggested = suggestColorFamily(hex);

  return (
    <div className="flex flex-col gap-3 rounded-card border border-brand-300 bg-brand-50/40 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={`${t('productName')} — عربي`} htmlFor="new-value-ar">
          <Input id="new-value-ar" dir="rtl" lang="ar" value={ar} onChange={(event) => setAr(event.target.value)} />
        </Field>
        <Field label={`${t('productName')} — English`} htmlFor="new-value-en" hint={en ? `${t('colourCode')}: ${colourCode(en)}` : undefined}>
          <Input id="new-value-en" dir="ltr" lang="en" value={en} onChange={(event) => setEn(event.target.value)} />
        </Field>
        {swatch ? (
          <>
            <Field label={t('swatch')} htmlFor="new-value-hex">
              <span className="flex items-center gap-2">
                <input
                  id="new-value-hex"
                  type="color"
                  value={/^#[0-9a-f]{6}$/i.test(hex) ? hex : '#000000'}
                  onChange={(event) => setHex(event.target.value)}
                  className="h-10 w-12 cursor-pointer rounded-control border border-line bg-surface"
                />
                <Input dir="ltr" value={hex} onChange={(event) => setHex(event.target.value)} className="w-28 font-mono" />
              </span>
            </Field>
            <Field
              label={t('colourFamily')}
              htmlFor="new-value-family"
              hint={
                suggested && !family
                  ? t('familySuggestion', { family: COLOR_FAMILIES.find((entry) => entry.code === suggested)?.[locale] ?? suggested })
                  : t('colourFamilyHint')
              }
            >
              <Select
                id="new-value-family"
                value={family || suggested || ''}
                onChange={(event) => setFamily(event.target.value as ColorFamilyCode)}
                className="w-full"
              >
                <option value="">{t('noFamily')}</option>
                {COLOR_FAMILIES.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry[locale]}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        ) : (
          <>
            <Field label={t('holdsLabel')} htmlFor="new-value-holds" hint={t('holdsHint')}>
              <Input id="new-value-holds" dir="ltr" inputMode="decimal" value={holds} onChange={(event) => setHolds(event.target.value)} className="w-28" />
            </Field>
            <div className="flex items-end justify-between gap-3 pb-2">
              <span className="text-sm text-ink">{t('sampleVariant')}</span>
              <Switch label={t('sampleVariant')} checked={isSample} onChange={setIsSample} />
            </div>
          </>
        )}
      </div>
      {error ? <Alert>{error}</Alert> : null}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button
          size="sm"
          disabled={saving || !en.trim() || !colourCode(en)}
          onClick={async () => {
            const value = await createLibraryValue(option.id, {
              translations: { en: en.trim(), ar: ar.trim() || null },
              ...(swatch
                ? { swatchHex: hex, family: (family || suggested || null) as ColorFamilyCode | null }
                : { measureQuantity: holds.trim() ? unitsToThousandths(holds) : null, isSample }),
            });
            if (value) onCreated(value);
          }}
        >
          {saving ? t('saving') : t('addToLibrary')}
        </Button>
      </div>
    </div>
  );
}

// ─── One value on this product ───────────────────────────────────────────────

/**
 * What the product says about one value: the library's name, the product's
 * own if it has one (ADR-052), a colour's photos, and removing it with every
 * variant it is sold as.
 */
function ValueSheet({
  product,
  axis,
  value,
  onClose,
}: {
  product: ProductDetailDto;
  axis: ProductOptionDto;
  value: string;
  onClose: () => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { setOptionValues, removeVariants, saving, error } = useCatalogStore();
  const entry = product.optionValues.find((row) => row.option === axis.code && row.value === value);
  const variants = variantsWith(product, axis.code, value);
  const fabric = axis.code === COLOR_OPTION ? fabricsOf(product).find((row) => row.color === value) : undefined;
  const [own, setOwn] = useState({
    ar: entry?.overridden ? (entry.translations?.ar ?? '') : '',
    en: entry?.overridden ? (entry.translations?.en ?? '') : '',
  });
  const [removing, setRemoving] = useState(false);

  /** Every override the product has, with this one set — or dropped. */
  function overrides(next: { ar: string; en: string } | null): OptionValueInput[] {
    const others = product.optionValues
      .filter((row) => row.overridden && !(row.option === axis.code && row.value === value))
      .map((row) => ({
        option: row.option,
        value: row.value,
        translations: { en: row.translations?.en || row.label, ar: row.translations?.ar || null },
        swatchHex: row.swatchHex,
      }));
    return next && next.en.trim()
      ? [...others, { option: axis.code, value, translations: { en: next.en.trim(), ar: next.ar.trim() || null }, swatchHex: entry?.swatchHex ?? null }]
      : others;
  }

  return (
    <Sheet
      open
      title={entry?.label ?? value}
      subtitle={`${axis.name} · ${value}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('close')}
          </Button>
          <Button
            disabled={saving || !own.en.trim()}
            onClick={async () => {
              if (await setOptionValues(product.id, overrides(own))) onClose();
            }}
          >
            {saving ? t('saving') : t('saveOwnName')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {error ? <Alert>{error}</Alert> : null}

        <div className="flex items-center gap-3">
          {axis.display === 'SWATCH' ? (
            <span className="size-12 rounded-full border border-line shadow-card" style={{ backgroundColor: entry?.swatchHex ?? 'transparent' }} />
          ) : null}
          <div>
            <p className="text-sm font-semibold text-ink">{entry?.label ?? value}</p>
            <p className="text-xs text-ink-muted">
              {entry?.overridden ? t('ownNameOnProduct') : t('libraryNameOnProduct')}
              {entry?.family ? ` · ${COLOR_FAMILIES.find((row) => row.code === entry.family)?.[locale] ?? ''}` : ''}
            </p>
          </div>
        </div>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-semibold text-ink">{t('ownNameTitle')}</legend>
          <p className="text-xs text-ink-muted">{t('ownNameHint')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              dir="rtl"
              lang="ar"
              placeholder="عربي"
              aria-label={`${t('ownNameTitle')} — عربي`}
              value={own.ar}
              onChange={(event) => setOwn({ ...own, ar: event.target.value })}
            />
            <Input
              dir="ltr"
              lang="en"
              placeholder="English"
              aria-label={`${t('ownNameTitle')} — English`}
              value={own.en}
              onChange={(event) => setOwn({ ...own, en: event.target.value })}
            />
          </div>
          {entry?.overridden ? (
            <Button
              variant="ghost"
              size="sm"
              className="self-start"
              disabled={saving}
              onClick={async () => {
                if (await setOptionValues(product.id, overrides(null))) onClose();
              }}
            >
              {t('useLibraryName')}
            </Button>
          ) : null}
        </fieldset>

        {fabric ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-ink">{t('mediaTitle')}</p>
            {fabric.images.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {fabric.images.map((image) => (
                  <Thumb key={image.id} src={mediaUrl(image.url)} size="lg" />
                ))}
              </div>
            ) : (
              <p className="text-xs text-ink-muted">{t('colourNoPhoto')}</p>
            )}
            <Link to="?tab=media" onClick={onClose} className="text-xs text-brand-700 hover:underline">
              {t('linkPhotosOnMedia')}
            </Link>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-ink">{t('variants')}</p>
          <ul className="divide-y divide-line rounded-control border border-line">
            {variants.map((variant) => (
              <li key={variant.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <code dir="ltr" className="text-xs text-ink-muted">
                  {variant.sku}
                </code>
                <span data-numeric className="text-ink">
                  {money(variant.price, locale, product.currency)}
                </span>
              </li>
            ))}
          </ul>
          <Button variant="secondary" className="self-start text-danger" onClick={() => setRemoving(true)} disabled={saving}>
            <TrashIcon className="size-4" />
            {t('removeValueWithVariants', { count: count(variants.length, locale) })}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={removing}
        title={t('removeColourTitle', { name: entry?.label ?? value })}
        body={t('removeColourBody', { count: count(variants.length, locale) })}
        confirmLabel={t('remove')}
        cancelLabel={t('cancel')}
        busy={saving}
        onCancel={() => setRemoving(false)}
        onConfirm={async () => {
          if (await removeVariants(variants.map((variant) => variant.id))) onClose();
          setRemoving(false);
        }}
      />
    </Sheet>
  );
}
